import RightArrowSvg from 'components/RightArrowSvg'
import SidebarLayout from 'components/SidebarLayout'
import { useApi } from 'providers/ApiProvider'
import { useState, useRef, useEffect, ChangeEvent } from 'react'
import { StyleUtil } from 'utils/StyleUtil'
import { ToastUtil } from 'utils/ToastUtil'
import Spinner from 'views/Spinner'
import React from 'react'
import DropDownMenu from '../views/DropDownMenu'
import excelSvg from 'images/excel.svg'
import { useCompositeAuth } from 'providers/CompositeAuthProvider'

function Grades() {
  const { isLogged } = useCompositeAuth()
  const {
    getCanvasClients,
    uploadGrades,
    getCanvasCourses,
    getCanvasAssignments,
    getCanvasMarkingSheet,
  } = useApi()
  const [selectedFile, setSelectedFile] = useState<any>(null)
  const [isUploading, setIsUploading] = useState<boolean>(false)
  const inputRef = useRef<HTMLInputElement | null>(null)

  const [canvasClients, setCanvasClients] = useState<any>([
    {
      id: 'none',
      name: 'No clients found',
      value: 'none',
    },
  ])
  const [selectedClient, setSelectedClient] = useState<any>(null)
  const [isClientsLoading, setIsClientsLoading] = useState<boolean>(false)
  const [canvasCourses, setCanvasCourses] = useState<any>([
    {
      id: 'none',
      name: 'No courses found',
      value: 'none',
    },
  ])
  const [selectedCourse, setSelectedCourse] = useState<any>(null)
  const [isCoursesLoading, setIsCoursesLoading] = useState<boolean>(false)
  const [canvasAssignments, setCanvasAssignments] = useState<any>([
    {
      id: 'none',
      name: 'No assignments found',
      value: 'none',
    },
  ])
  const [selectedAssignment, setSelectedAssignment] = useState<any>(null)
  const [isAssignmentsLoading, setIsAssignmentsLoading] =
    useState<boolean>(false)
  useEffect(() => {
    const fetchCanvasClients = async () => {
      setIsClientsLoading(true)
      let clients = await getCanvasClients()
      clients = clients.map((client: any) => ({
        ...client,
        id: client.uuid,
        name: client.name,
        value: client.conn_id,
      }))
      if (clients.length > 0) {
        setCanvasClients([...clients])
        setSelectedClient(null)
      } else {
        setCanvasClients([
          {
            id: 'none',
            name: 'No clients found',
            value: 'none',
          },
        ])
        setSelectedClient(null)
      }
      setIsClientsLoading(false)
    }
    if (isLogged) fetchCanvasClients()
  }, [isLogged, getCanvasClients])

  useEffect(() => {
    const fetchCanvasCourses = async (clientConnId: string) => {
      setIsCoursesLoading(true)
      let courses = await getCanvasCourses(clientConnId)
      courses = courses.map((course: any) => ({
        ...course,
        id: course.id,
        name: course.name,
        value: course.id,
      }))
      if (courses.length > 0) {
        setCanvasCourses(courses)
        setSelectedCourse(courses[0])
      } else {
        setCanvasCourses([
          {
            id: 'none',
            name: 'No courses found',
            value: 'none',
          },
        ])
        setSelectedCourse(null)
      }
      setIsCoursesLoading(false)
    }
    if (selectedClient?.id) fetchCanvasCourses(selectedClient.value)
    // else {
    //   setCanvasCourses([
    //     {
    //       id: 'none',
    //       name: 'No courses found',
    //       value: 'none',
    //     },
    //   ])
    //   setSelectedCourse(null)
    // }
  }, [selectedClient, getCanvasCourses])

  useEffect(() => {
    const fetchCanvasAssignments = async (courseId: string) => {
      setIsAssignmentsLoading(true)
      let assignments = await getCanvasAssignments(
        selectedClient.value,
        courseId,
      )
      assignments = assignments.map((assignment: any) => ({
        ...assignment,
        id: assignment.id,
        name: assignment.name,
        value: assignment.id,
      }))
      assignments = assignments.filter(
        (assignment: any) => assignment.rubric?.length > 0,
      )

      if (assignments.length > 0) {
        setCanvasAssignments(assignments)
        setSelectedAssignment(assignments[0])
      } else {
        setCanvasAssignments([
          {
            id: 'none',
            name: 'No assignments found',
            value: 'none',
          },
        ])
        setSelectedAssignment(null)
      }
      setIsAssignmentsLoading(false)
    }
    if (selectedClient?.id && selectedCourse)
      fetchCanvasAssignments(selectedCourse.value)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCourse, getCanvasAssignments])

  const hasFiles = (e: React.DragEvent<HTMLDivElement>) => {
    const types = e.dataTransfer.types
    return types.indexOf('Files') > -1
  }

  const onClickDownload = async (
    clientId: string,
    course: any,
    assignment: any,
  ) => {
    try {
      const blob = await getCanvasMarkingSheet(
        course.id,
        assignment.id,
        clientId,
      )
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url

      const filename = `${assignment.name}.xlsx`

      link.setAttribute('download', filename)
      document.body.appendChild(link)

      // Trigger the download
      link.click()

      // Cleanup
      link.parentNode?.removeChild(link)
      window.URL.revokeObjectURL(url)
    } catch (error) {
      console.error('Download failed:', error)
      ToastUtil.error('Download failed')
    }
  }

  return (
    <SidebarLayout>
      <div
        className={StyleUtil.containerNoScroll}
        style={{
          overflowY: 'auto',
        }}
      >
        <p className={StyleUtil.headline}>
          Upload an Excel file to import grades
        </p>
        {canvasClients.length === 0 && <Spinner />}
        {canvasClients.length > 0 && (
          <>
            <div
              className="my-6 flex"
              style={{
                justifyContent: 'space-between',
                alignItems: 'flex-start',
                flexWrap: 'wrap',
                gap: 12,
              }}
            >
              {!isClientsLoading ? (
                <DropDownMenu
                  testId="dropdown-client"
                  items={[
                    {
                      id: null,
                      name: 'Choose a client',
                      value: null,
                      isLabel: true,
                    },
                    ...canvasClients,
                  ]}
                  onSelected={async (item) => {
                    setSelectedClient(item)
                  }}
                  style={{
                    flexGrow: 1,
                  }}
                />
              ) : (
                <div
                  style={{
                    flexGrow: 1,
                    height: 34,
                    marginTop: -32,
                  }}
                >
                  <Spinner />
                </div>
              )}

              {!isClientsLoading && !isCoursesLoading ? (
                <DropDownMenu
                  testId="dropdown-course"
                  items={canvasCourses}
                  onSelected={(item) => {
                    setSelectedCourse(item)
                  }}
                  style={{
                    flexGrow: 4,
                  }}
                />
              ) : (
                <div
                  style={{
                    flexGrow: 4,
                    height: 34,
                    marginTop: -32,
                  }}
                >
                  <Spinner />
                </div>
              )}
              {!isClientsLoading &&
              !isCoursesLoading &&
              !isAssignmentsLoading ? (
                <DropDownMenu
                  testId="dropdown-assignment"
                  items={canvasAssignments}
                  onSelected={(item) => {
                    setSelectedAssignment(item)
                  }}
                  style={{
                    flexGrow: 1,
                  }}
                />
              ) : (
                <div
                  style={{
                    flexGrow: 1,
                    height: 34,
                    marginTop: -32,
                  }}
                >
                  <Spinner />
                </div>
              )}
            </div>
            <div className="flex justify-center">
              <button
                data-testid="btn-download-marking-sheet"
                className={
                  selectedAssignment
                    ? StyleUtil.buttonPrimaryUnsw
                    : StyleUtil.buttonPrimaryDisabled
                }
                onClick={() =>
                  onClickDownload(
                    selectedClient.value,
                    selectedCourse,
                    selectedAssignment,
                    // selectedCourse.students,
                  )
                }
                style={{
                  ...(!selectedAssignment && {
                    cursor: 'not-allowed',
                    pointerEvents: 'none',
                  }),
                  marginBottom: 24,
                }}
              >
                Download marking sheet
              </button>
            </div>
          </>
        )}

        <article
          style={{
            maxWidth: 560,
            margin: '0 auto',
            paddingTop: 24,
            paddingLeft: 36,
            paddingRight: 36,
            paddingBottom: 36,
            boxShadow:
              '0px 570px 160px 0px rgba(0, 0, 0, 0.00), 0px 365px 146px 0px rgba(0, 0, 0, 0.01), 0px 205px 123px 0px rgba(0, 0, 0, 0.05), 0px 91px 91px 0px rgba(0, 0, 0, 0.09), 0px 23px 50px 0px rgba(0, 0, 0, 0.10)',
          }}
          aria-label="File Upload Modal"
          className="relative flex flex-col bg-white"
          onDrop={(e: React.DragEvent<HTMLDivElement>) => {
            e.preventDefault()
            setSelectedFile(e.dataTransfer.files[0])
          }}
          onDragOver={(e: React.DragEvent<HTMLDivElement>) => {
            if (hasFiles(e)) {
              e.stopPropagation()
              e.preventDefault()
            }
          }}
          onDragEnter={(e: React.DragEvent<HTMLDivElement>) => {
            e.preventDefault()
            if (!hasFiles(e)) {
              return
            }
          }}
        >
          <section className="mx-auto flex h-full w-full flex-col overflow-hidden">
            <header className="border-1 flex h-[270px] flex-col items-center justify-center border border-gray-400">
              <p
                className="flex flex-wrap justify-center"
                style={{
                  fontWeight: 700,
                  fontSize: 20,
                }}
              >
                <span>Drag Excel here to upload</span>
              </p>
              <input
                data-testid="input-file-upload"
                id="hidden-input"
                title="hidden-input"
                type="file"
                // multiple={true}
                className="hidden"
                ref={inputRef}
                onInput={(e: ChangeEvent<HTMLInputElement>) => {
                  if (!e.target.files) {
                    return
                  }
                  const tmpFile = e.target.files[0] as any
                  if (!tmpFile) return
                  setSelectedFile(tmpFile)
                }}
              />
              <button
                data-testid="btn-select"
                type="button"
                id="btn-select"
                onClick={() => inputRef.current?.click()}
                className={`${StyleUtil.buttonPrimaryUnsw} mb-0 mt-[60px]`}
              >
                <span className="pl-2 pr-2">Select file</span>
              </button>
            </header>
            <div className="pt-6">Preview</div>
            {selectedFile ? (
              <div
                style={{
                  marginTop: 6,
                  marginBottom: 24,
                }}
              >
                <img
                  src={excelSvg}
                  alt={selectedFile.name}
                  style={{ display: 'inline-block', marginRight: 4 }}
                />
                <span
                  style={{
                    fontSize: 16,
                    fontWeight: 700,
                    verticalAlign: 'top',
                  }}
                >
                  {selectedFile.name}
                </span>
              </div>
            ) : (
              <div
                style={{
                  height: 50,
                }}
              ></div>
            )}
          </section>
          {isUploading && <Spinner />}
          <footer className="flex justify-center">
            <button
              data-testid="btn-submit"
              type="button"
              id="btn-submit"
              onClick={async () => {
                if (!selectedFile) {
                  ToastUtil.warning('Please select a file')
                  return
                }
                if (isUploading) return
                setIsUploading(true)
                const formData = new FormData()
                formData.append('file', selectedFile)
                try {
                  const data = await uploadGrades(formData)
                  console.log(data)
                  ToastUtil.success('Uploaded successfully')

                  if (data.errors?.length > 0) {
                    data.errors.forEach(() => {
                      console.error(data)
                      // ToastUtil.error(`${err.studentName}: ${err.error}`)
                    })
                  }
                } catch (err) {
                  console.error(err)
                  // setIsUploading(false)
                  const message = (err as any).message
                  if (message) {
                    // document format error
                    ToastUtil.error(`Upload failed`)
                    ToastUtil.error(message)
                  } else {
                    // fallback general error
                    ToastUtil.error(`Upload failed`)
                    console.error(err)
                  }
                } finally {
                  setIsUploading(false)
                  setSelectedFile(null)
                  // @ts-ignore
                  inputRef.current.value && (inputRef.current.value = '')
                }
              }}
              disabled={isUploading}
              className={StyleUtil.buttonPrimaryUnsw + ' !w-64'}
              style={{
                width: 258,
                display: 'flex',
                justifyContent: 'space-between',
              }}
            >
              <span className="pl-2 pr-2">Upload</span>
              <RightArrowSvg />
            </button>
          </footer>
        </article>
      </div>
    </SidebarLayout>
  )
}

export default Grades
